/* eslint-disable max-lines */
import { useIsHandOffAvailable } from '@/container/session-page/use-hand-off-availability';
import { useDictationHotkey } from '@/features/dictation/hooks/useDictationHotkey';
import { useIsDictationEnabled } from '@/features/dictation/hooks/useIsDictationEnabled';
import { useChordDisplayTokens } from '@/features/dictation/utils/useChordDisplayTokens';
import { useIsCmdShiftHOverlayEnabled, useIsProjectsEnabled } from '@/features/flags';
import { isTauri } from '@/tauri/utils';
import { useBackquoteKeyLabel } from '@heidi/dictate';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useIntl } from 'react-intl';
import { Key } from 'ts-key-enum';

export enum Scope {
  All = 'all',
  SessionPage = 'sessionPage',
  TemplateLibraryPage = 'templateLibraryPage',
  ProjectChatPage = 'projectChatPage',
  RefillsPage = 'refillsPage',
}

export const hotkeysList = {
  newSession: {
    hotkeys: ['n', `${Key.Meta}+${Key.Alt}+n`, `${Key.Control}+${Key.Alt}+n`],
    scopes: [Scope.All],
  },
  startStopRecording: {
    hotkeys: ['r', `${Key.Meta}+${Key.Alt}+r`, `${Key.Control}+${Key.Alt}+r`],
    scopes: [Scope.SessionPage],
  },
  startRecordingKeyInputForRouteNavigation: {
    hotkeys: ['g'],
    scopes: [Scope.All],
  },
  pauseUnpauseRecording: {
    hotkeys: ['p', `${Key.Meta}+${Key.Alt}+e`, `${Key.Control}+${Key.Alt}+e`],
    scopes: [Scope.SessionPage],
  },
  ptConsent: {
    hotkeys: ['y', `${Key.Meta}+${Key.Alt}+y`, `${Key.Control}+${Key.Alt}+y`],
    scopes: [Scope.SessionPage],
  },
  stopAndRestart: {
    // not implemented yet
    hotkeys: [`${Key.Meta}+0`],
    scopes: [Scope.SessionPage],
  },
  regenerateOutput: {
    hotkeys: [`${Key.Meta}+o`, `${Key.Control}+o`],
    scopes: [Scope.SessionPage],
  },
  changeTab: {
    hotkeys: {
      left: [`${Key.Meta}+${Key.Alt}+1`, `${Key.Control}+${Key.Alt}+1`],
      right: [`${Key.Meta}+${Key.Alt}+2`, `${Key.Control}+${Key.Alt}+2`],
    },
    scopes: [Scope.SessionPage],
  },
  moveTabLeft: {
    hotkeys: [`${Key.Meta}+${Key.Alt}+3`, `${Key.Control}+${Key.Alt}+3`],
    scopes: [Scope.SessionPage],
  },
  moveTabRight: {
    hotkeys: [`${Key.Meta}+${Key.Alt}+4`, `${Key.Control}+${Key.Alt}+4`],
    scopes: [Scope.SessionPage],
  },
  changeSession: {
    hotkeys: {
      up: [`${Key.Meta}+${Key.Alt}+5`, `${Key.Control}+${Key.Alt}+5`],
      down: [`${Key.Meta}+${Key.Alt}+6`, `${Key.Control}+${Key.Alt}+6`],
    },
    scopes: [Scope.SessionPage],
  },
  deleteSelectedSessions: {
    hotkeys: [Key.Delete, Key.Backspace],
    scopes: [Scope.All],
  },
  goToSessions: {
    hotkeys: ['g+s'],
    scopes: [Scope.All],
  },
  goToTemplateLibrary: {
    hotkeys: ['g+l'],
    scopes: [Scope.All],
  },
  goToTeam: {
    hotkeys: ['g+t'],
    scopes: [Scope.All],
  },
  goToSettings: {
    hotkeys: ['g+a'],
    scopes: [Scope.All],
  },
  goToCommunity: {
    hotkeys: ['g+c'],
    scopes: [Scope.All],
  },
  openShortcutsHelper: {
    hotkeys: ['s'],
    scopes: [Scope.All],
  },
  openSideNav: {
    hotkeys: ['h', `${Key.Meta}+${Key.Alt}+h`, `${Key.Control}+${Key.Alt}+h`],
    scopes: [Scope.All],
  },
  toggleControlPanel: {
    hotkeys: [`${Key.Meta}+${Key.Alt}+p`, `${Key.Control}+${Key.Alt}+p`],
    scopes: [Scope.SessionPage],
  },
  pinUnpinSidebarPanel: {
    hotkeys: [`${Key.Meta}+slash`, `${Key.Control}+slash`],
    scopes: [Scope.All],
  },
  createDocument: {
    hotkeys: [`${Key.Meta}+${Key.Alt}+7`],
    scopes: [Scope.SessionPage],
  },
  createTemplate: {
    hotkeys: [`${Key.Meta}+${Key.Alt}+7`],
    scopes: [Scope.TemplateLibraryPage],
  },
  newTab: {
    // not implemented yet
    hotkeys: [`${Key.Meta}+${Key.Shift}+${Key.Enter}`],
    scopes: [Scope.SessionPage],
  },
  toggleDictateEverywhere: {
    hotkeys: [`${Key.Meta}+${Key.Alt}+s`, `${Key.Control}+${Key.Alt}+s`],
    scopes: [Scope.SessionPage],
  },
  changeTemplate: {
    hotkeys: ['t', `${Key.Meta}+${Key.Alt}+t`, `${Key.Control}+${Key.Alt}+t`],
    scopes: [Scope.SessionPage],
  },
  handOff: {
    hotkeys: [`${Key.Meta}+${Key.Alt}+f`, `${Key.Control}+${Key.Alt}+f`],
    scopes: [Scope.SessionPage],
  },
  changeScribe: {
    hotkeys: [`${Key.Meta}+${Key.Alt}+w`, `${Key.Control}+${Key.Alt}+w`],
    scopes: [Scope.SessionPage],
  },
  leftBrain: {
    hotkeys: ['l', Key.ArrowLeft, Key.ArrowUp],
    scopes: [Scope.SessionPage],
  },
  rightBrain: {
    hotkeys: ['r', Key.ArrowRight, Key.ArrowDown],
    scopes: [Scope.SessionPage],
  },
  changeStyle: {
    hotkeys: ['v'],
    scopes: [Scope.SessionPage],
  },
  changeVoice: {
    hotkeys: [`${Key.Meta}+${Key.Alt}+v`, `${Key.Control}+${Key.Alt}+v`],
    scopes: [Scope.SessionPage],
  },
  documentPrint: {
    hotkeys: [`${Key.Meta}+${Key.Alt}+x`, `${Key.Control}+${Key.Alt}+x`],
    scopes: [Scope.All],
  },
  stopGenerating: {
    hotkeys: [Key.Escape],
    scopes: [Scope.All],
  },
  makeAsWorkflow: {
    hotkeys: [`${Key.Meta}+${Key.Shift}+w`, `${Key.Control}+${Key.Shift}+w`],
    scopes: [Scope.ProjectChatPage],
  },
  /**
   * Walking the Refills review queue. Unmodified keys, scoped to that page
   * only: the surface is a one-request-at-a-time reader meant to be worked
   * without a mouse, so the bindings have to be a single press. They are
   * advertised on the buttons they mirror rather than hidden in the shortcuts
   * helper.
   */
  refillsQueue: {
    hotkeys: {
      next: [Key.ArrowDown],
      prev: [Key.ArrowUp],
      // The two decisions in the contract. 'a' is unbound anywhere in the
      // app; 'r' is taken twice, but both are Scope.SessionPage, which is
      // never enabled on Refills. Neither is app-wide, so neither shadows a
      // key the clinician can rely on everywhere. There is no 'e': Escalate
      // is not a RepeatReviewStatus, so the key would write nothing.
      approve: ['a'],
      reject: ['r'],
    },
    scopes: [Scope.RefillsPage],
  },
  /** Desktop: owned by Tauri global shortcut (floating overlay). Web: in-app → Work. */
  summonHeidi: {
    hotkeys: [`${Key.Meta}+${Key.Shift}+h`, `${Key.Control}+${Key.Shift}+h`],
    scopes: [Scope.All],
  },
};

type KeyWithType =
  | { type: 'modifier'; key: Key }
  | { type: 'letter'; key: string }
  | { type: 'separator'; key: string };

const separator = (label: string): KeyWithType => ({ type: 'separator', key: label });

const MODIFIER_KEYS = new Set<string>([
  Key.Meta,
  Key.Shift,
  Key.ArrowUp,
  Key.ArrowDown,
  Key.ArrowLeft,
  Key.ArrowRight,
  Key.Alt,
]);

export const keyToKeyWithType = (key: string | Key): KeyWithType => {
  if (MODIFIER_KEYS.has(key)) {
    return { type: 'modifier', key: key as Key };
  }
  return { type: 'letter', key };
};

export const enumToKeyCode = {
  [Key.ArrowDown]: 40,
  [Key.ArrowUp]: 38,
  [Key.ArrowLeft]: 37,
  [Key.ArrowRight]: 39,
};

export const useShortcutsHelperList = () => {
  const { t } = useTranslation('common');
  const intl = useIntl();
  const { keyLabel: backquoteKeyLabel } = useBackquoteKeyLabel();
  const isDictationEnabled = useIsDictationEnabled();
  const isHandOffEnabled = useIsHandOffAvailable();
  const isProjectsEnabled = useIsProjectsEnabled();
  const isCmdShiftHOverlayEnabled = useIsCmdShiftHOverlayEnabled();
  // Desktop: Tauri overlay tip. Web: Work home is the Heidi summon surface.
  const showSummonHeidi = isTauri() ? isCmdShiftHOverlayEnabled : isProjectsEnabled;
  const isDesktop = isTauri();
  // Desktop: derive the label from the user's actual resolved chord (default
  // or customized via Settings) instead of hardcoding the platform default —
  // same pipeline as DictationHotkeyKbd / DictationLogsHeader.
  const { hotkey: dictationHotkey } = useDictationHotkey();
  const dictateChordTokens = useChordDisplayTokens(dictationHotkey);
  const dictateKeyItems = useMemo(
    () =>
      isDesktop
        ? dictateChordTokens.map((token) => keyToKeyWithType(token))
        : [keyToKeyWithType(backquoteKeyLabel)],
    [isDesktop, dictateChordTokens, backquoteKeyLabel]
  );

  return useMemo(
    () => ({
      // sessions
      sessions: {
        groupTitle: t('shortcuts.sessions'),
        items: [
          {
            value: t('shortcuts.newSession'),
            displayedKeys: {
              mac: [
                keyToKeyWithType('N'),
                separator('/'),
                keyToKeyWithType(Key.Meta),
                keyToKeyWithType(Key.Alt),
                keyToKeyWithType('N'),
              ],
              windows: [
                keyToKeyWithType('N'),
                separator('/'),
                keyToKeyWithType('Ctrl'),
                keyToKeyWithType(Key.Alt),
                keyToKeyWithType('N'),
              ],
            },
            scopes: [Scope.All],
          },
          {
            value: intl.formatMessage({
              defaultMessage: 'Start/end transcribing',
              id: 'ncvboznMaf',
            }),
            displayedKeys: {
              mac: [
                keyToKeyWithType('R'),
                separator('/'),
                keyToKeyWithType(Key.Meta),
                keyToKeyWithType(Key.Alt),
                keyToKeyWithType('R'),
              ],
              windows: [
                keyToKeyWithType('R'),
                separator('/'),
                keyToKeyWithType('Ctrl'),
                keyToKeyWithType(Key.Alt),
                keyToKeyWithType('R'),
              ],
            },
            scopes: [Scope.SessionPage],
          },
          {
            value: intl.formatMessage({
              defaultMessage: 'Pause/unpause transcribing',
              id: 'GI6xXjttaQ',
            }),
            displayedKeys: {
              mac: [
                keyToKeyWithType('P'),
                separator('/'),
                keyToKeyWithType(Key.Meta),
                keyToKeyWithType(Key.Alt),
                keyToKeyWithType('E'),
              ],
              windows: [
                keyToKeyWithType('P'),
                separator('/'),
                keyToKeyWithType('Ctrl'),
                keyToKeyWithType(Key.Alt),
                keyToKeyWithType('E'),
              ],
            },
            scopes: [Scope.SessionPage],
          },
          {
            value: t('shortcuts.togglePatientConsent'),
            displayedKeys: {
              mac: [
                keyToKeyWithType('Y'),
                separator('/'),
                keyToKeyWithType(Key.Meta),
                keyToKeyWithType(Key.Alt),
                keyToKeyWithType('Y'),
              ],
              windows: [
                keyToKeyWithType('Y'),
                separator('/'),
                keyToKeyWithType('Ctrl'),
                keyToKeyWithType(Key.Alt),
                keyToKeyWithType('Y'),
              ],
            },
            scopes: [Scope.SessionPage],
          },
          {
            value: t('shortcuts.changeTabs'),
            displayedKeys: {
              mac: [
                keyToKeyWithType(Key.Meta),
                keyToKeyWithType(Key.Alt),
                keyToKeyWithType('1'),
                separator('/'),
                keyToKeyWithType('2'),
              ],
              windows: [
                keyToKeyWithType('Ctrl'),
                keyToKeyWithType(Key.Alt),
                keyToKeyWithType('1'),
                separator('/'),
                keyToKeyWithType('2'),
              ],
            },
          },

          {
            value: t('shortcuts.splitToLeftRight'),
            displayedKeys: {
              mac: [
                keyToKeyWithType(Key.Meta),
                keyToKeyWithType(Key.Alt),
                keyToKeyWithType('3'),
                separator('/'),
                keyToKeyWithType('4'),
              ],
              windows: [
                keyToKeyWithType('Ctrl'),
                keyToKeyWithType(Key.Alt),
                keyToKeyWithType('3'),
                separator('/'),
                keyToKeyWithType('4'),
              ],
            },
          },

          {
            value: t('shortcuts.changeSession'),
            displayedKeys: {
              mac: [
                keyToKeyWithType(Key.Meta),
                keyToKeyWithType(Key.Alt),
                keyToKeyWithType('5'),
                separator('/'),
                keyToKeyWithType('6'),
              ],
              windows: [
                keyToKeyWithType('Ctrl'),
                keyToKeyWithType(Key.Alt),
                keyToKeyWithType('5'),
                separator('/'),
                keyToKeyWithType('6'),
              ],
            },
          },

          {
            value: t('shortcuts.deleteSelectedSessions'),
            displayedKeys: {
              mac: [keyToKeyWithType(Key.Delete)],
              windows: [keyToKeyWithType(Key.Delete)],
            },
          },
          // Hidden alongside every other hand off entry point when the
          // feature flag is off or the user has no team/org to hand off to.
          ...(isHandOffEnabled
            ? [
                {
                  value: intl.formatMessage({
                    defaultMessage: 'Hand off session',
                    id: 'yNeFUrmaai',
                    description: 'Shortcuts helper: open the hand off modal',
                  }),
                  displayedKeys: {
                    mac: [
                      keyToKeyWithType(Key.Meta),
                      keyToKeyWithType(Key.Alt),
                      keyToKeyWithType('F'),
                    ],
                    windows: [
                      keyToKeyWithType('Ctrl'),
                      keyToKeyWithType(Key.Alt),
                      keyToKeyWithType('F'),
                    ],
                  },
                  scopes: [Scope.SessionPage],
                },
              ]
            : []),
        ],
      },

      // dictate
      ...(isDictationEnabled
        ? {
            dictate: {
              groupTitle: t('shortcuts.dictate'),
              items: [
                {
                  value: t('shortcuts.dictateIntoTextField'),
                  displayedKeys: {
                    mac: [separator(t('shortcuts.hold')), ...dictateKeyItems],
                    windows: [separator(t('shortcuts.hold')), ...dictateKeyItems],
                  },
                },
                {
                  value: t('shortcuts.handsFree'),
                  displayedKeys: {
                    mac: [separator(t('shortcuts.doublePress')), ...dictateKeyItems],
                    windows: [separator(t('shortcuts.doublePress')), ...dictateKeyItems],
                  },
                },
                {
                  value: t('shortcuts.endHandsFree'),
                  displayedKeys: {
                    mac: [separator(t('shortcuts.press')), ...dictateKeyItems],
                    windows: [separator(t('shortcuts.press')), ...dictateKeyItems],
                  },
                },
              ],
            },
          }
        : {}),

      // general
      general: {
        groupTitle: t('shortcuts.general'),
        items: [
          {
            value: t('shortcuts.dismissOrGoBack'),
            displayedKeys: {
              mac: [keyToKeyWithType(Key.Escape)],
              windows: [keyToKeyWithType('Esc')],
            },
          },
          {
            value: t('shortcuts.copy'),
            displayedKeys: {
              mac: [keyToKeyWithType(Key.Meta), keyToKeyWithType('C')],
              windows: [keyToKeyWithType('Ctrl'), keyToKeyWithType('C')],
            },
          },
          {
            value: t('shortcuts.paste'),
            displayedKeys: {
              mac: [keyToKeyWithType(Key.Meta), keyToKeyWithType('V')],
              windows: [keyToKeyWithType('Ctrl'), keyToKeyWithType('V')],
            },
          },
          {
            value: t('shortcuts.undo'),
            displayedKeys: {
              mac: [keyToKeyWithType(Key.Meta), keyToKeyWithType('Z')],
              windows: [keyToKeyWithType('Ctrl'), keyToKeyWithType('Z')],
            },
          },
          {
            value: t('shortcuts.redo'),
            displayedKeys: {
              mac: [
                keyToKeyWithType(Key.Meta),
                keyToKeyWithType(Key.Shift),
                keyToKeyWithType('Z'),
              ],
              windows: [
                keyToKeyWithType('Ctrl'),
                keyToKeyWithType(Key.Shift),
                keyToKeyWithType('Z'),
              ],
            },
          },
          {
            value: t('shortcuts.openCloseSidebar'),
            displayedKeys: {
              mac: [
                keyToKeyWithType('H'),
                separator('/'),
                keyToKeyWithType(Key.Meta),
                keyToKeyWithType(Key.Alt),
                keyToKeyWithType('H'),
              ],
              windows: [
                keyToKeyWithType('H'),
                separator('/'),
                keyToKeyWithType('Ctrl'),
                keyToKeyWithType(Key.Alt),
                keyToKeyWithType('H'),
              ],
            },
          },
          {
            value: intl.formatMessage({
              defaultMessage: 'Open/close control panel',
              id: 'hcqDB3dg4e',
              description:
                'Shortcuts helper: toggle the right-side control panel on a Scribe session',
            }),
            displayedKeys: {
              mac: [
                keyToKeyWithType(Key.Meta),
                keyToKeyWithType(Key.Alt),
                keyToKeyWithType('P'),
              ],
              windows: [
                keyToKeyWithType('Ctrl'),
                keyToKeyWithType(Key.Alt),
                keyToKeyWithType('P'),
              ],
            },
          },
          {
            value: t('shortcuts.pinUnpinSidebarPanel'),
            displayedKeys: {
              mac: [keyToKeyWithType(Key.Meta), keyToKeyWithType('/')],
              windows: [keyToKeyWithType('Ctrl'), keyToKeyWithType('/')],
            },
          },
          {
            value: t('shortcuts.documentPrint'),
            displayedKeys: {
              mac: [
                keyToKeyWithType(Key.Meta),
                keyToKeyWithType(Key.Alt),
                keyToKeyWithType('X'),
              ],
              windows: [
                keyToKeyWithType('Ctrl'),
                keyToKeyWithType(Key.Alt),
                keyToKeyWithType('X'),
              ],
            },
          },
        ],
      },

      goTo: {
        groupTitle: t('shortcuts.goto'),
        items: [
          {
            value: t('shortcuts.goToSessions'),
            displayedKeys: {
              mac: [
                keyToKeyWithType('G'),
                separator(t('shortcuts.then')),
                keyToKeyWithType('S'),
              ],
              windows: [
                keyToKeyWithType('G'),
                separator(t('shortcuts.then')),
                keyToKeyWithType('S'),
              ],
            },
          },
          ...(showSummonHeidi
            ? [
                {
                  value: intl.formatMessage({
                    defaultMessage: 'Summon Heidi',
                    id: 'OqJChX3eC8',
                    description:
                      'Shortcuts helper: open Heidi overlay (desktop) or Work (web)',
                  }),
                  displayedKeys: {
                    mac: [
                      keyToKeyWithType(Key.Meta),
                      keyToKeyWithType(Key.Shift),
                      keyToKeyWithType('H'),
                    ],
                    windows: [
                      keyToKeyWithType('Ctrl'),
                      keyToKeyWithType(Key.Shift),
                      keyToKeyWithType('H'),
                    ],
                  },
                },
              ]
            : []),
          {
            value: t('shortcuts.goToTemplateLibrary'),
            displayedKeys: {
              mac: [
                keyToKeyWithType('G'),
                separator(t('shortcuts.then')),
                keyToKeyWithType('L'),
              ],
              windows: [
                keyToKeyWithType('G'),
                separator(t('shortcuts.then')),
                keyToKeyWithType('L'),
              ],
            },
          },
          ...[
            {
              value: t('shortcuts.goToSettings'),
              displayedKeys: {
                mac: [
                  keyToKeyWithType('G'),
                  separator(t('shortcuts.then')),
                  keyToKeyWithType('A'),
                ],
                windows: [
                  keyToKeyWithType('G'),
                  separator(t('shortcuts.then')),
                  keyToKeyWithType('A'),
                ],
              },
            },
          ],

          {
            value: t('shortcuts.goToTeam'),
            displayedKeys: {
              mac: [
                keyToKeyWithType('G'),
                separator(t('shortcuts.then')),
                keyToKeyWithType('T'),
              ],
              windows: [
                keyToKeyWithType('G'),
                separator(t('shortcuts.then')),
                keyToKeyWithType('T'),
              ],
            },
          },
          {
            value: t('shortcuts.goToCommunity'),
            displayedKeys: {
              mac: [
                keyToKeyWithType('G'),
                separator(t('shortcuts.then')),
                keyToKeyWithType('C'),
              ],
              windows: [],
            },
          },
        ],
      },

      // editing
      editing: {
        groupTitle: t('shortcuts.editing'),
        items: [
          {
            value: t('shortcuts.createDocumentTemplate'),
            displayedKeys: {
              mac: [
                keyToKeyWithType('C'),
                separator('/'),
                keyToKeyWithType(Key.Meta),
                keyToKeyWithType(Key.Alt),
                keyToKeyWithType('M'),
              ],
              windows: [
                keyToKeyWithType('C'),
                separator('/'),
                keyToKeyWithType('Ctrl'),
                keyToKeyWithType(Key.Alt),
                keyToKeyWithType('M'),
              ],
            },
          },
          {
            value: t('shortcuts.changeTemplate'),
            displayedKeys: {
              mac: [
                keyToKeyWithType('T'),
                separator('/'),
                keyToKeyWithType(Key.Meta),
                keyToKeyWithType(Key.Alt),
                keyToKeyWithType('T'),
              ],
              windows: [
                keyToKeyWithType('T'),
                separator('/'),
                keyToKeyWithType('Ctrl'),
                keyToKeyWithType(Key.Alt),
                keyToKeyWithType('T'),
              ],
            },
          },
          {
            value: t('shortcuts.changeScribe'),
            displayedKeys: {
              mac: [
                keyToKeyWithType(Key.Meta),
                keyToKeyWithType(Key.Alt),
                keyToKeyWithType('W'),
              ],
              windows: [
                keyToKeyWithType('Ctrl'),
                keyToKeyWithType(Key.Alt),
                keyToKeyWithType('W'),
              ],
            },
          },
          {
            value: t('shortcuts.changeVoice'),
            displayedKeys: {
              mac: [
                keyToKeyWithType(Key.Meta),
                keyToKeyWithType(Key.Alt),
                keyToKeyWithType('V'),
              ],
              windows: [
                keyToKeyWithType('Ctrl'),
                keyToKeyWithType(Key.Alt),
                keyToKeyWithType('V'),
              ],
            },
          },
          {
            value: t('shortcuts.startDictating'),
            displayedKeys: {
              mac: [
                keyToKeyWithType(Key.Meta),
                keyToKeyWithType(Key.Alt),
                keyToKeyWithType('S'),
              ],
              windows: [
                keyToKeyWithType(Key.Control),
                keyToKeyWithType(Key.Alt),
                keyToKeyWithType('S'),
              ],
            },
          },
        ],
      },

      // formatting
      formatting: {
        groupTitle: t('shortcuts.formatting'),
        items: [
          {
            value: t('shortcuts.bold'),
            displayedKeys: {
              mac: [keyToKeyWithType(Key.Meta), keyToKeyWithType('B')],
              windows: [keyToKeyWithType('Ctrl'), keyToKeyWithType('B')],
            },
          },
          {
            value: t('shortcuts.italic'),
            displayedKeys: {
              mac: [keyToKeyWithType(Key.Meta), keyToKeyWithType('I')],
              windows: [keyToKeyWithType('Ctrl'), keyToKeyWithType('I')],
            },
          },
          {
            value: t('shortcuts.underline'),
            displayedKeys: {
              mac: [keyToKeyWithType(Key.Meta), keyToKeyWithType('U')],
              windows: [keyToKeyWithType('Ctrl'), keyToKeyWithType('U')],
            },
          },
        ],
      },
    }),
    [t, intl, isDictationEnabled, isHandOffEnabled, dictateKeyItems, showSummonHeidi]
  );
};
